import { makeStyles } from '@material-ui/core/styles'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import DateAndTime from 'core/components/listTable/cells/DateCell'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import Text from 'core/elements/Text'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { listByoHosts } from './actions'
import AddBYOHHosts from './AddBYOHHosts'
import { physicalNodeSelector, UNASSIGNED_CLUSTER_NAME } from './selectors'
import RadioDropdownFilter, {
  Item,
} from 'app/plugins/openstack/components/networks/dns/records/RadioDropdownFilter'

type ModelDataKey = DataKeys.KaapiBYOHosts
type SelectorModel = ArrayElement<ReturnType<typeof physicalNodeSelector>>
type ActionParams = InferActionParams<typeof listByoHosts>
type Params = ActionParams & {}
const defaultParams: Params = {
  orderBy: 'metadata.creationTimestamp',
  orderDirection: 'desc',
}
const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'KaapiBYOHosts',
  listTablePrefs,
)

const HostStatusIndicator = ({ value }: GridCellProps<SelectorModel, string>) => {
  const classes = useStyles({ state: value })
  return (
    <Text variant="body2">
      <FontAwesomeIcon className={classes.icon} solid>
        circle
      </FontAwesomeIcon>
      {value}
    </Text>
  )
}

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'uid',
    label: 'UID',
    display: false,
  },
  {
    key: 'clusterName',
    label: 'Cluster',
    CellComponent: ({ value }) => {
      return value !== UNASSIGNED_CLUSTER_NAME ? (
        <SimpleLink
          src={routeManager.routes.kubernetes.infrastructure.cluster.path({ cluster: value })}
        >
          {value}
        </SimpleLink>
      ) : (
        <Text variant="body2">{value}</Text>
      )
    },
  },
  {
    key: 'hostStatus',
    label: 'Host Status',
    CellComponent: HostStatusIndicator,
  },
  {
    key: 'byoMachine.metadata.name',
    label: 'Machine',
    display: false,
  },
  {
    key: 'namespace',
    label: 'Namespace',
    display: false,
  },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    CellComponent: ({ value }) => {
      return (
        <>
          {value.map((iface) => (
            <Text key={iface} variant="body2">
              {iface}
            </Text>
          ))}
        </>
      )
    },
  },
  {
    key: 'status.hostinfo.osimage',
    label: 'OS Image',
  },
  {
    key: 'status.hostinfo.osname',
    label: 'OS Name',
  },
  {
    key: 'metadata.creationTimestamp',
    label: 'Created',
    CellComponent: DateAndTime,
  },
]

const searchTargets = [
  'name',
  'cluster',
  'namespace',
  'hostStatus',
  'ipAddresses',
  'osImage',
  'osName',
  'uid',
]

const PhysicalHostsListPage = () => {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [filteredData, setFilteredData] = useState([])
  const { loading, reload } = useListAction(listByoHosts)
  const physicalNodes = useSelector(physicalNodeSelector)

  // Set filtered data on load
  useEffect(() => {
    setFilteredData(physicalNodes)
  }, [physicalNodes.length])

  // Set initial load
  useEffect(() => {
    reload(true)
  }, [])

  // Get cluster options
  const clusterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          physicalNodes?.flatMap((machine) => machine?.clusterName || UNASSIGNED_CLUSTER_NAME),
        ),
      ).map((clusterName) => ({
        label: clusterName,
        value: clusterName,
      })),
    [physicalNodes],
  )

  // Get filtered data based on selected cluster
  const handleApplyClusterFilter = useCallback(
    (selectedItems: Item[]) => {
      if (!selectedItems?.length) {
        setFilteredData([])
        return
      }
      const selectedClusterNames = selectedItems?.map((item) => item?.value)
      setFilteredData(
        physicalNodes?.filter((vm) => selectedClusterNames?.includes(vm?.clusterName)),
      )
    },
    [physicalNodes],
  )

  return (
    <>
      <DocumentMeta title="Physical Hosts" />
      <PollingData hidden loading={loading} onReload={reload} refreshDuration={1000 * 5} />
      <HeaderRefreshPortal>
        <RadioDropdownFilter
          width={300}
          label="Cluster"
          items={clusterOptions}
          loading={loading}
          data={physicalNodes}
          handleApplyFilter={handleApplyClusterFilter}
          showLabel={false}
          align="left"
        />
      </HeaderRefreshPortal>
      <ListContainer<ModelDataKey, SelectorModel>
        searchTargets={searchTargets}
        uniqueIdentifier="name"
        loading={loading}
        loadingMessage="Loading Physical Hosts..."
        onRefresh={reload}
        hideRefreshButtonOnTop={true}
        data={filteredData}
        columns={columns}
        getParamsUpdater={getParamsUpdater}
        label="Physical Hosts"
        showItemsCountInLabel
        disableRowSelection
        showRowMenuForSingleRowActions
        {...pick(listTablePrefs, params)}
        addText="Add Physical Host"
        AddDialogComponent={AddBYOHHosts}
      />
    </>
  )
}

export default PhysicalHostsListPage

const getIconOrBubbleColor = (status: string, theme: Theme) =>
  ({
    Connected: theme.palette.green.main,
    Disconnected: theme.palette.red.main,
  }[status] || theme.palette.grey[500])

const useStyles = makeStyles<Theme, { state?: string }>((theme: Theme) => ({
  icon: {
    fontSize: 12,
    marginRight: theme.spacing(0.5),
    color: ({ state }) => getIconOrBubbleColor(state, theme),
  },
}))
